import React from 'react';
import { GripVertical, RotateCcw } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';

// ── Data Model ───────────────────────────────────────────────

export interface ColumnSettings {
  order: string[];
  hidden: string[];
}

// ── localStorage Helpers ─────────────────────────────────────

const STORAGE_KEY = 'feedwatch-col-settings';

function getStore(): Record<string, ColumnSettings> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function loadColumnSettings(feedId: string): ColumnSettings | null {
  return getStore()[feedId] ?? null;
}

export function saveColumnSettings(feedId: string, settings: ColumnSettings): void {
  const store = getStore();
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...store, [feedId]: settings }));
}

export function clearColumnSettings(feedId: string): void {
  const store = getStore();
  const { [feedId]: _, ...rest } = store;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
}

// ── Sortable Row ─────────────────────────────────────────────

interface SortableColumnRowProps {
  columnKey: string;
  isVisible: boolean;
  label: string;
  onToggle: () => void;
}

function SortableColumnRow({ columnKey, isVisible, label, onToggle }: SortableColumnRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: columnKey });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
        isDragging ? 'bg-muted shadow-md z-50 relative' : 'hover:bg-muted/50'
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        className="p-0.5 cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground touch-none"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <Checkbox
        checked={isVisible}
        onCheckedChange={onToggle}
        className="shrink-0"
      />
      <span
        className={`flex-1 text-sm truncate select-none ${
          isVisible ? 'text-foreground' : 'text-muted-foreground line-through'
        }`}
      >
        {label}
      </span>
    </div>
  );
}

// ── Dialog Component ─────────────────────────────────────────

interface ColumnSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feedId: string;
  allColumns: string[];
  settings: ColumnSettings | null;
  onSettingsChange: (s: ColumnSettings | null) => void;
  formatHeader: (key: string) => string;
}

export function ColumnSettingsDialog({
  open,
  onOpenChange,
  feedId,
  allColumns,
  settings,
  onSettingsChange,
  formatHeader,
}: ColumnSettingsDialogProps) {
  const orderedColumns = React.useMemo(() => {
    if (!settings) return allColumns;
    const existing = settings.order.filter((k) => allColumns.includes(k));
    const newCols = allColumns.filter((k) => !settings.order.includes(k));
    return [...existing, ...newCols];
  }, [allColumns, settings]);

  const hiddenSet = new Set(settings?.hidden ?? []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const persist = (updated: ColumnSettings) => {
    saveColumnSettings(feedId, updated);
    onSettingsChange(updated);
  };

  const handleToggle = (key: string) => {
    const currentHidden = settings?.hidden ?? [];
    const newHidden = hiddenSet.has(key)
      ? currentHidden.filter((k) => k !== key)
      : [...currentHidden, key];
    persist({ order: orderedColumns, hidden: newHidden });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedColumns.indexOf(String(active.id));
    const newIndex = orderedColumns.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = [...orderedColumns];
    const [moved] = newOrder.splice(oldIndex, 1);
    newOrder.splice(newIndex, 0, moved);
    persist({ order: newOrder, hidden: settings?.hidden ?? [] });
  };

  const handleReset = () => {
    clearColumnSettings(feedId);
    onSettingsChange(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Column Settings</DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={handleReset}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Reset to Default
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-0.5 py-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={orderedColumns} strategy={verticalListSortingStrategy}>
              {orderedColumns.map((key) => (
                <SortableColumnRow
                  key={key}
                  columnKey={key}
                  isVisible={!hiddenSet.has(key)}
                  label={formatHeader(key)}
                  onToggle={() => handleToggle(key)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-lg" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
